import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* AirSpace Live — standalone marketing homepage for the Chrome extension.
   Mounted at /airspace-live-app as its own route (no app shell, no sidebar).
   Self-contained military/tactical theme: it ships its own scoped CSS via a
   <style> block (the .asl-* namespace) so it doesn't depend on the app's
   light-mode design tokens. Content comes verbatim from the extension's
   store listing (title, summary, description, features). */

const APP_NAME = "AirSpace Live";
const SUMMARY =
  "A live radar of the aircraft overhead, on every new tab. Enter a postcode and tap a blip to see what it is.";

const FEATURES = [
  {
    icon: "MapPin",
    title: "Hyper-Local Tracking",
    body:
      "Enter your postcode to center the radar perfectly on your immediate area.",
  },
  {
    icon: "Target",
    title: "Identify on Click",
    body:
      "Tap any radar blip to instantly reveal its flight number, aircraft type, altitude, and destination.",
  },
  {
    icon: "Shield",
    title: "Military Aesthetics",
    body:
      "Clean, dark-mode tactical UI inspired by modern defense tracking systems and AWACS displays.",
  },
  {
    icon: "Zap",
    title: "Zero Lag",
    body:
      "Highly optimized to load instantly every time you open a new tab without slowing down your browsing.",
  },
];

const STEPS = [
  { n: "01", title: "Install the extension", body: "Add AirSpace Live to Chrome in one click." },
  { n: "02", title: "Open a new tab", body: "Enter your postcode to lock the radar onto your area." },
  {
    n: "03",
    title: "Watch the skies live",
    body: "Click any aircraft to pull up its full flight profile.",
  },
];

const ASL_JSONLD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: APP_NAME,
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  description: SUMMARY,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

// A handful of decorative radar blips. Positions are percentages within the
// scope circle; each gets a slightly different pulse delay for life.
const BLIPS = [
  { top: "28%", left: "62%", delay: "0s", strong: true },
  { top: "44%", left: "34%", delay: "0.6s" },
  { top: "66%", left: "58%", delay: "1.2s" },
  { top: "38%", left: "76%", delay: "1.8s" },
  { top: "72%", left: "40%", delay: "0.9s" },
];

function AirSpaceLiveScreen() {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="asl-page screen-enter">
      <Seo
        title={APP_NAME}
        description={SUMMARY}
        keywords={[
          "live aircraft radar",
          "flight tracker chrome extension",
          "new tab aircraft tracker",
          "planespotting tool",
          "real-time air traffic",
          "tactical flight radar",
        ]}
        jsonLd={ASL_JSONLD}
      />

      <style>{ASL_CSS}</style>

      {/* ---------- Top bar ---------- */}
      <header className="asl-top">
        <button
          type="button"
          className="asl-back"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/");
          }}
          aria-label="Go back"
        >
          <window.I.ArrowLeft size={14} />
          <span>Back</span>
        </button>
        <div className="asl-brand">
          <span className="asl-brand-mark" aria-hidden="true" />
          <span className="asl-brand-name">{APP_NAME}</span>
        </div>
      </header>

      <main className="asl-shell">
        {/* ---------- Hero ---------- */}
        <section className="asl-hero">
          <div className="asl-hero-copy">
            <div className="asl-eyebrow">
              <span className="asl-eyebrow-dot" />
              <span>Tactical Aircraft Radar</span>
            </div>
            <h1 className="asl-title">
              The sky overhead,
              <br />
              <span className="asl-title-accent">live on every new tab.</span>
            </h1>
            <p className="asl-lede">{SUMMARY}</p>
            <div className="asl-cta-row">
              <a
                className="asl-btn asl-btn-primary"
                href="https://chrome.google.com/webstore"
                target="_blank"
                rel="noopener noreferrer"
              >
                <window.I.Zap size={15} />
                Add to Chrome
              </a>
              <a className="asl-btn asl-btn-ghost" href="#how">
                See how it works
              </a>
            </div>
          </div>

          {/* ---------- Radar scope ---------- */}
          <div className="asl-scope" aria-hidden="true">
            <div className="asl-scope-rings">
              <span className="asl-ring asl-ring-1" />
              <span className="asl-ring asl-ring-2" />
              <span className="asl-ring asl-ring-3" />
              <span className="asl-crosshair-h" />
              <span className="asl-crosshair-v" />
              <span className="asl-sweep" />
              {BLIPS.map((b, i) => (
                <span
                  key={i}
                  className={`asl-blip${b.strong ? " asl-blip-strong" : ""}`}
                  style={{ top: b.top, left: b.left, animationDelay: b.delay }}
                />
              ))}
            </div>
            <div className="asl-scope-readout">
              <span className="asl-readout-live">
                <span className="asl-readout-dot" /> LIVE
              </span>
              <span>5 CONTACTS · ALT 0–38,000 FT</span>
            </div>
          </div>
        </section>

        {/* ---------- Core features ---------- */}
        <section className="asl-features">
          <div className="asl-section-head">
            <span className="asl-section-tag">📡 Core Features</span>
            <h2 className="asl-section-title">
              Turn a new tab into an air traffic command center.
            </h2>
          </div>
          <div className="asl-grid">
            {FEATURES.map((f) => {
              const Icon = window.I[f.icon] || window.I.Target;
              return (
                <article className="asl-card" key={f.title}>
                  <span className="asl-card-icon">
                    <Icon size={18} />
                  </span>
                  <h3 className="asl-card-title">{f.title}</h3>
                  <p className="asl-card-body">{f.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ---------- How it works ---------- */}
        <section className="asl-how" id="how">
          <div className="asl-section-head">
            <span className="asl-section-tag">🛠️ How It Works</span>
            <h2 className="asl-section-title">Three steps to clear the skies.</h2>
          </div>
          <div className="asl-steps">
            {STEPS.map((s) => (
              <div className="asl-step" key={s.n}>
                <span className="asl-step-num">{s.n}</span>
                <div className="asl-step-content">
                  <h3 className="asl-step-title">{s.title}</h3>
                  <p className="asl-step-body">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- Privacy ---------- */}
        <section className="asl-privacy">
          <span className="asl-privacy-icon">
            <window.I.Lock size={18} />
          </span>
          <div>
            <h3 className="asl-privacy-title">Privacy First</h3>
            <p className="asl-privacy-body">
              AirSpace Live only uses your postcode to center the radar map
              locally. Your location data never leaves your device and is never
              tracked or stored.
            </p>
          </div>
        </section>

        {/* ---------- Closing ---------- */}
        <section className="asl-closing">
          <h2 className="asl-closing-title">
            Clear the skies. Own your new tab. ✈️
          </h2>
          <a
            className="asl-btn asl-btn-primary asl-btn-lg"
            href="https://chrome.google.com/webstore"
            target="_blank"
            rel="noopener noreferrer"
          >
            <window.I.Zap size={16} />
            Add AirSpace Live to Chrome
          </a>
        </section>
      </main>

      <footer className="asl-foot">
        <span className="asl-brand-mark" aria-hidden="true" />
        <span>{APP_NAME}</span>
        <Link to="/" className="asl-foot-link">
          Community Ninja
        </Link>
      </footer>
    </div>
  );
}

/* Scoped tactical theme. Military green on near-black, monospace HUD type. */
const ASL_CSS = `
.asl-page {
  --asl-bg: #070a07;
  --asl-panel: #0d130c;
  --asl-line: #1d2a18;
  --asl-green: #6f9e3f;
  --asl-green-bright: #9ee35a;
  --asl-text: #d6e2c8;
  --asl-muted: #7c8a6c;
  min-height: 100vh;
  background:
    radial-gradient(1200px 600px at 70% -10%, rgba(111,158,63,0.10), transparent 60%),
    var(--asl-bg);
  color: var(--asl-text);
  font-family: "JetBrains Mono", "SFMono-Regular", ui-monospace, Menlo, monospace;
}
.asl-top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid var(--asl-line);
}
.asl-back {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: 1px solid var(--asl-line); border-radius: 6px;
  color: var(--asl-muted); padding: 6px 12px; cursor: pointer;
  font: inherit; font-size: 12px; letter-spacing: 0.04em;
  transition: border-color .15s, color .15s;
}
.asl-back:hover { color: var(--asl-green-bright); border-color: var(--asl-green); }
.asl-brand { display: inline-flex; align-items: center; gap: 9px; }
.asl-brand-name {
  font-size: 13px; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--asl-green-bright); font-weight: 600;
}
.asl-brand-mark {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--asl-green-bright);
  box-shadow: 0 0 10px 2px rgba(158,227,90,0.6);
}
.asl-shell { max-width: 1080px; margin: 0 auto; padding: 0 24px 80px; }

/* Hero */
.asl-hero {
  display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 48px;
  align-items: center; padding: 72px 0 64px;
}
.asl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--asl-green); margin-bottom: 22px;
}
.asl-eyebrow-dot {
  width: 7px; height: 7px; border-radius: 50%; background: var(--asl-green-bright);
  box-shadow: 0 0 8px rgba(158,227,90,0.7); animation: asl-pulse 1.8s infinite;
}
.asl-title {
  font-size: 44px; line-height: 1.08; margin: 0; font-weight: 700;
  letter-spacing: -0.01em; color: #eef4e4;
}
.asl-title-accent { color: var(--asl-green-bright); }
.asl-lede {
  margin: 22px 0 30px; font-size: 15px; line-height: 1.6;
  color: var(--asl-muted); max-width: 46ch;
}
.asl-cta-row { display: flex; flex-wrap: wrap; gap: 12px; }
.asl-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 20px; border-radius: 7px; font: inherit; font-size: 13px;
  letter-spacing: 0.03em; cursor: pointer; text-decoration: none;
  border: 1px solid transparent; transition: transform .12s, background .15s, border-color .15s;
}
.asl-btn:active { transform: translateY(1px); }
.asl-btn-primary {
  background: var(--asl-green); color: #08120a; font-weight: 600;
  box-shadow: 0 0 22px rgba(111,158,63,0.35);
}
.asl-btn-primary:hover { background: var(--asl-green-bright); }
.asl-btn-ghost { border-color: var(--asl-line); color: var(--asl-text); }
.asl-btn-ghost:hover { border-color: var(--asl-green); color: var(--asl-green-bright); }
.asl-btn-lg { padding: 14px 26px; font-size: 14px; }

/* Radar scope */
.asl-scope { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.asl-scope-rings {
  position: relative; width: 320px; height: 320px; max-width: 100%;
  border-radius: 50%; border: 1px solid var(--asl-line);
  background:
    radial-gradient(circle, rgba(111,158,63,0.08) 0%, transparent 70%),
    var(--asl-panel);
  overflow: hidden;
}
.asl-ring {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  border: 1px solid rgba(111,158,63,0.28); border-radius: 50%;
}
.asl-ring-1 { width: 75%; height: 75%; }
.asl-ring-2 { width: 50%; height: 50%; }
.asl-ring-3 { width: 25%; height: 25%; }
.asl-crosshair-h, .asl-crosshair-v {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  background: rgba(111,158,63,0.22);
}
.asl-crosshair-h { width: 100%; height: 1px; }
.asl-crosshair-v { width: 1px; height: 100%; }
.asl-sweep {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: conic-gradient(from 0deg, rgba(158,227,90,0.35), transparent 28%);
  border-radius: 50%; animation: asl-sweep 3.4s linear infinite;
  transform-origin: center;
}
.asl-blip {
  position: absolute; width: 8px; height: 8px; border-radius: 50%;
  background: var(--asl-green-bright); transform: translate(-50%,-50%);
  box-shadow: 0 0 8px 2px rgba(158,227,90,0.7);
  animation: asl-blink 2.4s ease-in-out infinite;
}
.asl-blip-strong { width: 11px; height: 11px; box-shadow: 0 0 12px 3px rgba(158,227,90,0.85); }
.asl-scope-readout {
  display: flex; align-items: center; gap: 16px;
  font-size: 10.5px; letter-spacing: 0.12em; color: var(--asl-muted);
  text-transform: uppercase;
}
.asl-readout-live { display: inline-flex; align-items: center; gap: 6px; color: var(--asl-green-bright); }
.asl-readout-dot {
  width: 6px; height: 6px; border-radius: 50%; background: var(--asl-green-bright);
  animation: asl-pulse 1.4s infinite;
}

/* Sections */
.asl-section-head { margin-bottom: 28px; }
.asl-section-tag {
  font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--asl-green);
}
.asl-section-title {
  margin: 12px 0 0; font-size: 26px; line-height: 1.2; font-weight: 700;
  color: #eef4e4; max-width: 22ch;
}
.asl-features, .asl-how { padding: 28px 0; }
.asl-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;
}
.asl-card {
  background: var(--asl-panel); border: 1px solid var(--asl-line);
  border-radius: 10px; padding: 22px;
  transition: border-color .15s, transform .15s;
}
.asl-card:hover { border-color: var(--asl-green); transform: translateY(-2px); }
.asl-card-icon {
  display: inline-grid; place-items: center; width: 38px; height: 38px;
  border-radius: 9px; background: rgba(111,158,63,0.12);
  color: var(--asl-green-bright); border: 1px solid var(--asl-line); margin-bottom: 14px;
}
.asl-card-title { margin: 0 0 8px; font-size: 15px; color: #eef4e4; font-weight: 600; }
.asl-card-body { margin: 0; font-size: 13px; line-height: 1.55; color: var(--asl-muted); }

/* Steps */
.asl-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.asl-step {
  display: flex; gap: 14px; padding: 20px; border-radius: 10px;
  background: var(--asl-panel); border: 1px solid var(--asl-line);
}
.asl-step-num {
  font-size: 20px; font-weight: 700; color: var(--asl-green);
  letter-spacing: 0.02em; line-height: 1;
}
.asl-step-title { margin: 0 0 6px; font-size: 14px; color: #eef4e4; font-weight: 600; }
.asl-step-body { margin: 0; font-size: 12.5px; line-height: 1.5; color: var(--asl-muted); }

/* Privacy */
.asl-privacy {
  display: flex; gap: 16px; align-items: flex-start; margin: 36px 0;
  padding: 24px; border-radius: 12px;
  background: linear-gradient(180deg, rgba(111,158,63,0.08), transparent);
  border: 1px solid var(--asl-line);
}
.asl-privacy-icon {
  display: inline-grid; place-items: center; width: 40px; height: 40px;
  border-radius: 10px; background: rgba(111,158,63,0.14);
  color: var(--asl-green-bright); flex: none;
}
.asl-privacy-title { margin: 0 0 6px; font-size: 15px; color: #eef4e4; font-weight: 600; }
.asl-privacy-body { margin: 0; font-size: 13px; line-height: 1.6; color: var(--asl-muted); max-width: 64ch; }

/* Closing */
.asl-closing { text-align: center; padding: 56px 0 24px; }
.asl-closing-title { margin: 0 0 28px; font-size: 28px; font-weight: 700; color: #eef4e4; }

/* Footer */
.asl-foot {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 28px 24px; border-top: 1px solid var(--asl-line);
  font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--asl-muted);
}
.asl-foot-link { color: var(--asl-green); text-decoration: none; }
.asl-foot-link:hover { color: var(--asl-green-bright); }

@keyframes asl-sweep { to { transform: rotate(360deg); } }
@keyframes asl-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
@keyframes asl-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }

@media (max-width: 820px) {
  .asl-hero { grid-template-columns: 1fr; gap: 36px; padding: 48px 0; }
  .asl-title { font-size: 34px; }
  .asl-grid, .asl-steps { grid-template-columns: 1fr; }
  .asl-scope-rings { width: 280px; height: 280px; }
}
`;

window.AirSpaceLiveScreen = AirSpaceLiveScreen;

export default AirSpaceLiveScreen;
